import { AffiliateCoupon } from '@/components/activity/ActivityPages/20240229AffiliateCoupon/data.config';
import LongTermDiscount from '@/components/activity/ActivityPages/2024LongTermDiscount/data.config';
import Q4BFGiftsCareForUS from '@/components/activity/ActivityPages/2024Q4BFGiftsCareForUS/data.config';
import { isEmpty } from '@/utils';
import { armsErrorSend } from '@/utils/libs/armsErrorSend';

import { EasterSale } from '../ActivityPages/2025Q2EasterSale/data.config';
import GiftTShirt from '../ActivityPages/2025TShirtGifts/data.config';
import CampaignBase from '../CampaignBase';
import HideLimitedOffer from '../HideLimitedOffer/config';
import styles from './index.module.less';

import type { CampaignBaseProps, DiscIntro, ProductInfo, fromPage, WithCid, WithId, Label } from '../type';
import type { Commodity, ProductDetail } from '@/types/product';

/**
 * 1. 大促上线时，需要注意产品页常驻活动的营销文案，有可能大促上线期间常驻营销文案需要隐藏！！！
 * 2. 活动优先级按自然顺序即自上而下。
 */
class CampaignWrap {
  campaignWrap: Record<string, CampaignBase>;
  country: TBasicCountryInfo;
  isProd: boolean;
  LANG: LANG;
  insrc: string;
  pageCommonConfig: object;

  constructor(props: CampaignBaseProps) {
    this.country = props?.country ?? {};
    this.isProd = props?.isProd ?? true;
    this.LANG = props?.LANG ?? {};
    this.insrc = props?.insrc ?? '';
    this.pageCommonConfig = props.pageCommonConfig ?? {};

    // 初始化活动所需参数
    const commonParm: CampaignBaseProps = {
      isProd: this.isProd,
      country: this.country,
      LANG: this.LANG,
      insrc: this.insrc,
      urlQuery: props.urlQuery ?? {},
      pageCommonConfig: this.pageCommonConfig,
      productDetail: props?.productDetail ?? {},
      curSelectCommodityId: props?.curSelectCommodityId ?? -1,
      isGrayTest: props?.isGrayTest ?? false,
    };

    // 日常活动，需要隐藏产品页常驻活动营销文案
    const dailyCampaign: Record<string, CampaignBase> = {
      EasterSale: new EasterSale(commonParm),
    };

    // 特殊活动，不需要隐藏产品页常驻活动营销文案
    const specialCampaign: Record<string, CampaignBase> = {
      HideLimitedOffer: new HideLimitedOffer(commonParm),
      AffiliateCoupon: new AffiliateCoupon(commonParm), // 目前暂时没有在生效特殊分销折扣活动
      GiftTShirt: new GiftTShirt(commonParm),
      Q4BFGiftsCareForUS: new Q4BFGiftsCareForUS(commonParm),
    };

    // 常驻（长期）活动
    const residentCampaign: Record<string, CampaignBase> = {
      LongTermDiscount: new LongTermDiscount(commonParm),
    };

    // 是否需要隐藏常驻活动营销板块
    const isHideResidentCampaign = Object.values(dailyCampaign).some((_campaign) => {
      return _campaign?.enabled;
    });

    // 日常活动上线时，常驻活动营销板块隐藏
    if (isHideResidentCampaign) {
      Object.values(residentCampaign).forEach((_campaign) => {
        _campaign.getProductDiscountIntro = () => null;
      });
    }

    // 初始化活动集合
    this.campaignWrap = Object.assign(dailyCampaign, specialCampaign, residentCampaign) ?? {};
  }

  /**
   * 活动是否可用
   */
  enableCampaigns = (productInfo: ProductInfo, from: fromPage) => {
    const { pid, cid, productDetail, curSelectCommodity } = productInfo;
    return Object.values(this.campaignWrap).some((_campaign) =>
      _campaign.enableProductPage?.(
        {
          pid,
          cid,
          productDetail,
          curSelectCommodity,
        },
        from
      )
    );
  };

  /**
   * 获取活动倒计时组件参数
   */
  getCountDownProps = (productInfo: ProductInfo) => {
    const { pid, cid, productDetail, curSelectCommodity } = productInfo;
    const enableCampaigns = Object.values(this.campaignWrap)?.filter((item) =>
      item.enableProductPage?.(
        {
          pid,
          cid,
          curSelectCommodity,
          productDetail,
        },
        'countdown'
      )
    );
    return enableCampaigns?.[0]?.getCountDownProps?.({ pid, cid }) ?? {};
  };

  /**
   * 获取产品页套餐减价文案
   */
  getCampaignExtraInfo = (productInfo: ProductInfo) => {
    const { pid, cid, productDetail, curSelectCommodity } = productInfo;
    const intro = [];
    const during_campaign = Object.values(this.campaignWrap)?.filter((_campaign) => {
      return (
        _campaign.enableProductPage?.(
          {
            pid,
            cid,
            productDetail,
            curSelectCommodity,
          },
          'intro'
        ) && !isEmpty(_campaign?.getProductIntro?.({ pid, cid, curSelectCommodity }), true)
      );
    });
    const text = during_campaign?.[0]?.getProductIntro?.({ pid, cid, curSelectCommodity });
    if (!isEmpty(text, true)) intro.push(text);
    return intro;
  };

  /**
   * 获取产品页营销模块信息配置（多个活动同时展示
   */
  getCampaignDiscountInfoAfterActivity = (productInfo: ProductInfo) => {
    const { pid, cid, productDetail, curSelectCommodity } = productInfo;
    const list: Array<object> = [];
    const during_campaign = Object.values(this.campaignWrap)?.filter((_campaign) => {
      return (
        _campaign.enableProductPage?.(
          {
            pid,
            cid,
            productDetail,
            curSelectCommodity,
          },
          'discountIntro'
        ) && !isEmpty(_campaign?.getDiscountInfoAfterActivity?.({ pid, cid, curSelectCommodity }), true)
      );
    });

    during_campaign.forEach((_c) => {
      const intro = _c?.getDiscountInfoAfterActivity?.({ pid, cid, curSelectCommodity });
      if (typeof intro === 'object') {
        // 数组列表（同一个活动多个营销信息）
        if (Array.isArray(intro)) {
          intro.forEach((o) => list.push(o));
        }
        // json
        if (!isEmpty(intro?.text, true)) {
          list.push(intro);
        }
      } else if (!isEmpty(intro, true) && typeof intro === 'string') {
        list.push(intro);
      }
    });

    return list;
  };

  /**
   * 获取产品页营销模块信息配置（多个活动同时展示）
   */
  getCampaignDiscountInfo = (productInfo: ProductInfo) => {
    const { pid, cid, productDetail, curSelectCommodity } = productInfo;
    const info_top: Array<DiscIntro> = [];
    const info_bottom: Array<DiscIntro> = [];

    const during_campaign = Object.values(this.campaignWrap)?.filter((_campaign) => {
      return (
        _campaign.enableProductPage?.(
          {
            pid,
            cid,
            productDetail,
            curSelectCommodity,
          },
          'discountIntro'
        ) && !isEmpty(_campaign?.getProductDiscountIntro?.({ pid, cid, curSelectCommodity }), true)
      );
    });

    during_campaign.forEach((_c) => {
      const intro = _c?.getProductDiscountIntro?.({ pid, cid, curSelectCommodity });
      if (typeof intro === 'object') {
        // 数组列表（同一个活动多个营销信息）
        if (Array.isArray(intro)) {
          intro.forEach((o) => (o?.isTop ? info_top.push(o) : info_bottom.push(o)));
        }
        // json
        if (!isEmpty(intro?.text || intro?.html, true)) {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          intro?.isTop ? info_top.push(intro) : info_bottom.push(intro);
        }
      } else if (!isEmpty(intro, true) && typeof intro === 'string') {
        info_bottom.push(intro);
      }
    });
    return [info_top, info_bottom];
  };

  /**
   * 获取活动标签(多个活动取第一个对这个套餐有配置的)
   */
  getTagInfosGroup = ({
    tagInfosGroup,
    commodity,
    product,
  }: {
    tagInfosGroup: object[];
    commodity: Commodity;
    product: ProductDetail;
  }) => {
    for (let i = 0; i < Object.values(this.campaignWrap).length; i++) {
      const _campaign = Object.values(this.campaignWrap)[i];
      const labelObj = _campaign.getCommodityLabel?.(commodity, tagInfosGroup as Label[], this.LANG);
      if (
        _campaign.enableProductPage?.(
          {
            pid: product?.id,
            productDetail: product,
            cid: commodity.id,
            curSelectCommodity: commodity,
          },
          'tag'
        ) &&
        !isEmpty(labelObj)
      ) {
        if (labelObj?.replaceLabels) {
          tagInfosGroup = labelObj.replaceLabels;
        } else if (!isEmpty(labelObj?.index)) {
          tagInfosGroup?.splice(labelObj?.index as number, 0, labelObj?.label as object);
        } else {
          tagInfosGroup?.push(labelObj?.label as object);
        }
        break;
      }
    }
    return tagInfosGroup;
  };

  /**
   * 产品页是否隐藏tradeup
   */
  getEnableTradeUp = ({ productId }: { productId: number }) => {
    return Object.values(this.campaignWrap)?.filter((_campaign) =>
      _campaign?.enableProductPage?.(
        {
          pid: productId,
        },
        'tradeup'
      )
    )?.[0];
  };

  /**
   * 获取虚拟服务弹窗减价文案
   */
  getServeModalText = (serviceItem: Array<WithCid>) => {
    let text = '';
    Object.values(this.campaignWrap)?.forEach((_campaign) => {
      serviceItem.forEach((item) => {
        const _text = _campaign.getCartServiceText?.(item?.cid, 'modal');
        if (!isEmpty(_text, true)) {
          text = _text as string;
        }
      });
    });
    return text;
  };

  /**
   * 获取购物车减价文案
   */
  getCartServiceText = (service: WithId) => {
    const during_campaign = Object.values(this.campaignWrap)?.filter(
      (_campaign) => !isEmpty(_campaign.getCartServiceText?.(service.id), true)
    );

    return during_campaign?.[0]?.getCartServiceText?.(service.id);
  };

  /**
   * 获取移动端浮窗
   */
  getHeaderEntry = (product: ProductDetail) => {
    const during_campaign = Object.values(this.campaignWrap)?.filter((_campaign) =>
      _campaign?.enableHeaderEntry?.(product)
    )?.[0];
    return during_campaign?.getHeaderEntry?.() || null;
  };

  /**
   * 获取导航购物车浮窗内展示文字
   */
  getCartCardText = (selectCartItems: Array<unknown>, cartItems: Array<unknown>) => {
    const during_campaign = Object.values(this.campaignWrap)?.filter(
      (_campaign) => !!_campaign?.getCartCardText?.(selectCartItems, cartItems)
    )?.[0];
    const text = during_campaign?.getCartCardText?.(selectCartItems, cartItems) || '';
    return text ? <div className={styles.cartCardText} dangerouslySetInnerHTML={{ __html: text }} /> : null;
  };

  /**
   * 获取购物车金额底部的折扣展示文字
   */
  getCartPageText = (selectCartItems: Array<unknown>, cartItems: Array<unknown>) => {
    try {
      const during_campaign = Object.values(this.campaignWrap)?.filter(
        (_campaign) => !!_campaign?.getCartPageText?.(selectCartItems, cartItems)
      )?.[0];
      const text = during_campaign?.getCartPageText?.(selectCartItems, cartItems) || '';

      return text ? <div style={{ textAlign: 'right' }} dangerouslySetInnerHTML={{ __html: text }} /> : null;
    } catch (e) {
      armsErrorSend(e as Error, { filename: 'CampaignWrap getCartPageText', data: { selectCartItems } });
      return null;
    }
  };

  /**
   * 获取支付方式右上角的红色标签
   */
  getPaymentItemTag = (type: string, orderItems = [], orderGifts = [], area = this.country.code) => {
    try {
      const during_campaign = Object.values(this.campaignWrap)?.filter(
        (_campaign) => !isEmpty(_campaign?.getPaymentItemTag?.(type, orderItems, orderGifts || [], area), true)
      )?.[0];
      const text = during_campaign?.getPaymentItemTag?.(type, orderItems, orderGifts || [], area) || '';
      return text ? <div className={styles.discountTag} dangerouslySetInnerHTML={{ __html: text }} /> : null;
    } catch (e) {
      armsErrorSend(e as Error, { filename: 'activity getPaymentItemTag', data: { type, orderItems, orderGifts } });
      return null;
    }
  };

  /**
   * 排序支付列表
   */
  sortPaymentMethods = (
    PaymentMethods: Array<unknown>,
    orderItems: Array<unknown>,
    orderConfirmGifts: Array<unknown>,
    area = this.country.code
  ) => {
    try {
      const during_campaign = Object.values(this.campaignWrap)?.filter(
        (_campaign) => !isEmpty(_campaign?.sortPaymentMethods?.(PaymentMethods, orderItems, orderConfirmGifts, area))
      )?.[0];
      if (!during_campaign) return [];
      return during_campaign
        ? during_campaign.sortPaymentMethods(PaymentMethods, orderItems, orderConfirmGifts, area)
        : [];
    } catch (e) {
      armsErrorSend(e as Error, {
        filename: 'activity sortPaymentMethods',
        data: { PaymentMethods, orderItems, orderConfirmGifts },
      });
      return PaymentMethods;
    }
  };
}

export default CampaignWrap;
